// Where a business can put its assistant, as a slow carousel of drawings.
//
// Accessible the way auto-rotating content has to be (WCAG 2.2.2): it pauses
// while the pointer or keyboard focus is on it, has its own pause button, and
// does not move at all for someone who has asked their system for reduced
// motion. Every drawing is in the markup from the start, so the prerendered
// home page (client/scripts/prerender.mjs) carries all five images and their
// alt texts for anyone reading it without JavaScript; only the first slide's
// caption is rendered until the carousel moves.

import { useEffect, useState } from "react";
import { HERO_SLIDES, type HeroSlide } from "../lib/marketing";

export const INTERVAL_MS = 5500;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

export default function HeroCarousel({ slides = HERO_SLIDES, intervalMs = INTERVAL_MS }: { slides?: HeroSlide[]; intervalMs?: number }) {
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [stopped, setStopped] = useState(false);
  const reduced = usePrefersReducedMotion();
  const rotating = !stopped && !reduced && slides.length > 1;

  useEffect(() => {
    if (!rotating || hovered || focused) return;
    const timer = window.setTimeout(() => setIndex((current) => (current + 1) % slides.length), intervalMs);
    return () => window.clearTimeout(timer);
  }, [index, rotating, hovered, focused, intervalMs, slides.length]);

  const go = (next: number) => setIndex((next + slides.length) % slides.length);
  const current = slides[index];

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Where your assistant can answer"
      className="relative min-w-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-[1.75rem] shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200 dark:ring-slate-700">
        {slides.map((slide, i) => (
          <img
            key={slide.src}
            src={slide.src}
            alt={slide.alt}
            width={1120}
            height={836}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            aria-hidden={i === index ? undefined : true}
            className={
              "absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out motion-reduce:transition-none " +
              (i === index ? "opacity-100" : "opacity-0")
            }
          />
        ))}
      </div>

      {/* Caption beside the controls on wider screens; above them on a phone,
          where sharing the row squeezed the caption into a narrow column. */}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <p aria-live={rotating && !hovered && !focused ? "off" : "polite"} className="text-left sm:min-h-[3.5rem]">
          <span className="block font-semibold text-slate-900">{current.title}</span>
          <span className="block text-sm text-slate-600">{current.body}</span>
        </p>

        <div className="-ml-2 flex shrink-0 items-center gap-1.5 sm:ml-0">
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="Previous"
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
          >
            <Chevron direction="left" />
          </button>
          {slides.map((slide, i) => (
            <button
              key={slide.src}
              type="button"
              onClick={() => go(i)}
              aria-label={`Show ${slide.title.toLowerCase()}`}
              aria-current={i === index ? "true" : undefined}
              // The dot is what shows; the button around it is 40px tall so a
              // thumb can hit it, with negative margins so the row looks as
              // it did.
              className="group -mx-1 -my-1 flex h-10 items-center justify-center px-2"
            >
              <span
                aria-hidden="true"
                className={
                  "block h-2.5 rounded-full transition-all " +
                  (i === index ? "w-6 bg-brand-600" : "w-2.5 bg-slate-300 group-hover:bg-slate-400")
                }
              />
            </button>
          ))}
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="Next"
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
          >
            <Chevron direction="right" />
          </button>
          {!reduced && slides.length > 1 && (
            <button
              type="button"
              onClick={() => setStopped((value) => !value)}
              aria-label={stopped ? "Play slideshow" : "Pause slideshow"}
              className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            >
              {stopped ? <PlayIcon /> : <PauseIcon />}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function Chevron({ direction }: { direction: "left" | "right" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={direction === "left" ? "M12.5 4.5 7 10l5.5 5.5" : "M7.5 4.5 13 10l-5.5 5.5"} />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
      <rect x="5" y="4" width="3.5" height="12" rx="1" />
      <rect x="11.5" y="4" width="3.5" height="12" rx="1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
      <path d="M6 4.5v11a1 1 0 0 0 1.5.86l9-5.5a1 1 0 0 0 0-1.72l-9-5.5A1 1 0 0 0 6 4.5Z" />
    </svg>
  );
}
